import React, { useState, useEffect } from 'react'
import { Modal, Button, Form } from 'react-bootstrap'
import toast from 'react-hot-toast'
import Cookies from 'js-cookie'
import ApiEndpoints, {BASE_URL} from './commonComponents/ApiEndpoints'

const EditInstituteModal = ({ show, handleClose, institute }) => {
  const [updatedInstitute, setUpdatedInstitute] = useState({
    name: '',
    address: '',
    pincode: '',
  })
  const [logoFile, setLogoFile] = useState(null)
  const [stampFile, setStampFile] = useState(null)

  useEffect(() => {
    if (institute) {
      setUpdatedInstitute({
        name: institute.name || '',
        address: institute.address || '',
        pincode: institute.pincode || '',
      })
    }
  }, [institute])

  useEffect(() => {
    // Reset selected file when switching institutes / reopening modal
    setLogoFile(null)
    setStampFile(null)
  }, [show, institute])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setUpdatedInstitute((prev) => ({ ...prev, [name]: value }))
  }

  const instituteLogoRaw =
    institute?.institute_logo || institute?.logo || institute?.url || ''
  const instituteLogoSrc =
    typeof instituteLogoRaw === 'string' &&
    (instituteLogoRaw.startsWith('http://') ||
      instituteLogoRaw.startsWith('https://'))
      ? instituteLogoRaw
      : ''

  const stampUrlRaw = institute?.stamp_url_img || ''
  const stampSrc =
    typeof stampUrlRaw === 'string' &&
    (stampUrlRaw.startsWith('http://') || stampUrlRaw.startsWith('https://'))
      ? stampUrlRaw
      : ''

  const handleSaveChanges = async () => {
    try {
      let payload = updatedInstitute
      if (logoFile) {
        if (logoFile.size > 2 * 1024 * 1024) {
          toast.error('Logo image size should not exceed 2MB.')
          return
        }

        try {
          const formData = new FormData()
          // Virtual Prachar API expects multipart field name: `image`
          formData.append('image', logoFile)

          const logoResponse = await ApiEndpoints.uploadInstituteLogo(
            institute.institute_id,
            formData,
          )

          const logoUrl =
            logoResponse.data?.url ?? logoResponse.data?.data?.url ?? null

          if (logoUrl) {
            payload = { ...updatedInstitute, logo: logoUrl }
            Cookies.set('institute_logo', logoUrl, { expires: 7 })
            window.location.reload()
          } else {
            toast.error(
              'Institute saved, but logo URL was not returned from the server.',
            )
          }
        } catch (logoErr) {
          toast.error(
            logoErr.response?.data?.message ||
              logoErr.message ||
              'Logo upload failed.',
          )
        }
      }

      if (stampFile) {
        if (stampFile.size > 2 * 1024 * 1024) {
          toast.error('Stamp image size should not exceed 2MB.')
          return
        }

        try {
          const formData = new FormData()
          // Virtual Prachar API expects multipart field name: `image`
          formData.append('image', stampFile)

          const stampResponse = await ApiEndpoints.uploadInstituteLogo(
            institute.institute_id,
            formData,
          )

          const stampUrl =
            stampResponse.data?.url ?? stampResponse.data?.data?.url ?? null

          if (stampUrl) {
            payload = { ...payload, stamp_url_img: stampUrl }
            Cookies.set('institute_stamp', stampUrl, { expires: 7 })
          } else {
            toast.error(
              'Institute saved, but stamp URL was not returned from the server.',
            )
          }
        } catch (stampErr) {
          toast.error(
            stampErr.response?.data?.message ||
              stampErr.message ||
              'Stamp upload failed.',
          )
        }
      }

      const response = await ApiEndpoints.editInstitute(
        institute.institute_id,
        payload,
      )
      toast.success(response.data.message)
      handleClose()
      window.location.reload()
    } catch (error) {
      toast.error(error.response?.message || 'Something wents wrong.')
    }
  }

  const handleCancel = () => {
    setLogoFile(null)
    handleClose()
  }

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton 
        closeVariant="white"
        className="bg-warning text-dark">
        <Modal.Title>Edit Institute</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* Class Name Field */}
          <Form.Group className="mb-3">
            <Form.Label>Institute Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={updatedInstitute.name}
              onChange={handleInputChange}
              placeholder="Enter Institute Name"
              className="p-2 border rounded"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Address</Form.Label>
            <Form.Control
              type="textarea"
              name="address"
              value={updatedInstitute.address}
              onChange={handleInputChange}
              placeholder="Enter Address"
              className="p-2 border rounded"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Pincode</Form.Label>
            <Form.Control
              type="number"
              name="pincode"
              value={updatedInstitute.pincode}
              onChange={handleInputChange}
              placeholder="Enter Pincode"
              className="p-2 border rounded"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Institute logo (optional)</Form.Label>
            <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
              <Form.Control
                type="file"
                accept="image/png, image/jpeg, image/jpg, image/webp"
                onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
                className="p-2 border rounded"
              />
              {(logoFile || instituteLogoSrc) && (
                <img
                  src={logoFile ? URL.createObjectURL(logoFile) : instituteLogoSrc}
                  alt="Logo preview"
                  style={{
                    width: 72,
                    height: 72,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '1px solid #ccc',
                  }}
                />
              )}
            </div>
            <Form.Text className="text-muted">Max 2MB</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Institute Stamp (optional)</Form.Label>
            <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
              <Form.Control
                type="file"
                accept="image/png, image/jpeg, image/jpg, image/webp"
                onChange={(e) => setStampFile(e.target.files?.[0] ?? null)}
                className="p-2 border rounded"
              />
              {(stampFile || stampSrc) && (
                <img
                  src={stampFile ? URL.createObjectURL(stampFile) : stampSrc}
                  alt="Logo preview"
                  style={{
                    width: 72,
                    height: 72,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '1px solid #ccc',
                  }}
                />
              )}
            </div>
            <Form.Text className="text-muted">Max 2MB</Form.Text>
          </Form.Group>

        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="warning" onClick={handleSaveChanges}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default EditInstituteModal
